import { useState } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { trackEvent } from '@fs/zion-analytics'
import { useFeatureFlag } from '@fs/zion-flags'
import { handleRotate, setMemoryCache, uploadPhoto } from '../../services/MemoriesAPI/memoriesAPI'
import { useMemoryBook } from '../../providers/MemoryBookProvider'

const useImageHandlers = ({
  memoryId,
  setSavedImage,
  setWaitingOnRotation,
  rotation,
  setSelectedImage,
  updatePageContentProps: { pageNumber, positionIndex },
  cropArea,
}) => {
  const statusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const { personId: pid } = useUser()
  const [artifactId, setArtifactId] = useState(memoryId || null)
  const { updatePageContent, setMemoryViewerMemoryId } = useMemoryBook()
  const cropImagesFlag = useFeatureFlag('chinese_memoryBooks_cropImages')?.isOn

  const appendCacheBuster = (url) => {
    if (!url || cropImagesFlag) {
      return url
    }

    return `${url}&_=${Date.now()}`
  }

  const showError = (messageKey, defaultMessage) => {
    statusOverlay({
      message: t(messageKey, defaultMessage),
      type: 'error',
    })
  }

  const updateImage = (artifact) => {
    if (artifact?.url) {
      artifact.url = appendCacheBuster(artifact.url)
    }
    setSelectedImage(artifact)
    setArtifactId(artifact?.id)
  }

  const onUpload = async (file) => {
    if (!file || !pid) return

    try {
      const { artifact, success } = await uploadPhoto({ file, pid, name: file.name })
      if (!success) throw new Error()

      updateImage(artifact)
      trackEvent({
        event_name: 'memories_upload_photo',
      })
    } catch (error) {
      const messageKey =
        error?.response?.status === 413
          ? 'ImagePickerOverlay.onUpload.failedLargeFile'
          : 'ImagePickerOverlay.onUpload.failedGeneral'
      const defaultMessage =
        error?.response?.status === 413
          ? 'Image uploads cannot be larger than 15MB.'
          : 'An error occurred while uploading the file.'
      showError(messageKey, defaultMessage)
    }
  }

  const onRemove = () => {
    if (!pid) {
      showError('ImagePickerOverlay.onRemove.failed', 'An error occurred while removing the image.')
      return
    }
    setSavedImage(null)
    setMemoryViewerMemoryId(null)
  }

  const onSave = async (image) => {
    if (!image || !pid) return

    let shouldRotate = false

    try {
      const memoryToUpdateId = artifactId || memoryId
      let updatedMemory = null

      if (!memoryToUpdateId) {
        showError('ImagePickerOverlay.onSave.failed', 'An error occurred while saving the image.')
        return
      }

      shouldRotate = Boolean(!cropImagesFlag && rotation && rotation > 0 && memoryToUpdateId)

      if (shouldRotate && typeof setWaitingOnRotation === 'function') {
        setWaitingOnRotation(true)
      }

      if (shouldRotate) {
        updatedMemory = await handleRotate({ memoryId: memoryToUpdateId, rotationDegrees: rotation })
        if (updatedMemory?.url) {
          updatedMemory.url = appendCacheBuster(updatedMemory.url)
        }
      }

      setMemoryViewerMemoryId(memoryToUpdateId)

      const imageToSave = updatedMemory || image

      setSavedImage(imageToSave)
      setMemoryCache(memoryToUpdateId, imageToSave)

      const updates = { memoryId: memoryToUpdateId }
      if (cropImagesFlag && cropArea !== undefined) {
        updates.cropArea = cropArea
      }

      await updatePageContent(pageNumber, positionIndex, updates)
      trackEvent({
        event_name: 'memories_save_photo',
      })
    } catch (error) {
      console.error('Error during save:', error)
      showError('ImagePickerOverlay.onSave.failed', 'An error occurred while saving the image.')
    } finally {
      if (shouldRotate && typeof setWaitingOnRotation === 'function') {
        setWaitingOnRotation(false)
      }
    }
  }

  return { onUpload, onRemove, onSave, updateImage }
}

export default useImageHandlers
